/**
 * The two non-secret pieces of auth state that must survive a page reload.
 *
 * The access token is NOT here — it stays in memory (`./token`) so an XSS bug
 * cannot read it out of storage. What survives instead is the httpOnly refresh
 * cookie plus these two hints, neither of which grants anything on its own.
 */

const WORKSPACE_KEY = 'cyphex.workspace';
const CSRF_COOKIE = 'cxt_csrf';

/** Which workspace to rotate against; also pre-fills the sign-in form. */
export function getWorkspaceSlug(): string | null {
  try {
    return globalThis.localStorage?.getItem(WORKSPACE_KEY) ?? null;
  } catch {
    return null;
  }
}

export function setWorkspaceSlug(slug: string | null): void {
  try {
    if (slug === null) globalThis.localStorage?.removeItem(WORKSPACE_KEY);
    else globalThis.localStorage?.setItem(WORKSPACE_KEY, slug);
  } catch {
    /* Private mode or storage disabled. The slug is a convenience, not state. */
  }
}

/**
 * The readable half of the double-submit CSRF pair. The API sets `cxt_csrf`
 * WITHOUT httpOnly precisely so this can copy it into a request header, which
 * a cross-site page cannot do.
 */
export function readCsrfToken(): string | null {
  const cookies = globalThis.document?.cookie;
  if (typeof cookies !== 'string') return null;
  const match = cookies.split('; ').find((entry) => entry.startsWith(`${CSRF_COOKIE}=`));
  if (match === undefined) return null;
  const value = match.slice(CSRF_COOKIE.length + 1);
  return value.length > 0 ? decodeURIComponent(value) : null;
}
